import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiX, HiShoppingBag } from 'react-icons/hi';
import ImageLightbox from './ImageLightbox';
import { categoryLabel } from '../utils/categories';
import { isPremium } from '../utils/productMap';
import useScrollLock from '../utils/useScrollLock';
import '../styles/ProductModal.css';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/**
 * Everything the shop card has to truncate: the full description, every
 * tag, the exact stock, and both actions.
 */
export default function ProductModal({ product, onAddToCart, onClose }) {
  const [zoom, setZoom] = useState(false);
  const [added, setAdded] = useState(false);
  const [imageBroken, setImageBroken] = useState(false);

  useScrollLock(!!product);

  useEffect(() => {
    // The lightbox handles its own Escape; don't close both at once.
    const onKey = (e) => {
      if (e.key === 'Escape' && !zoom) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, zoom]);

  if (!product) return null;

  const hasImage = !!product.image && !imageBroken;

  const stock = Number.isFinite(Number(product.stock))
    ? Number(product.stock)
    : null;
  const outOfStock = product.inStock === false || stock === 0;

  const discount =
    product.originalPrice && product.originalPrice > product.price
      ? Math.round(
          ((product.originalPrice - product.price) / product.originalPrice) * 100
        )
      : 0;

  const handleAdd = () => {
    onAddToCart(product);
    setAdded(true);
  };

  return (
    <>
      <div className="pm-overlay" onClick={onClose}>
        <motion.div
          className="pm-modal"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={product.name}
        >
          <button className="pm-close" onClick={onClose} aria-label="Close">
            <HiX />
          </button>

          <div
            className={`pm-image ${hasImage ? 'zoomable' : ''}`}
            onClick={() => hasImage && setZoom(true)}
            role={hasImage ? 'button' : undefined}
            tabIndex={hasImage ? 0 : undefined}
            aria-label={hasImage ? `Zoom into ${product.name}` : undefined}
            onKeyDown={(e) => {
              if (hasImage && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                setZoom(true);
              }
            }}
          >
            {hasImage ? (
              <img
                src={product.image}
                alt={product.name}
                onError={() => setImageBroken(true)}
              />
            ) : (
              /* Same emoji fallback the shop card uses, so a dead image
                 URL doesn't show a broken-image icon. */
              <div className="pm-image-fallback">{product.emoji || '💎'}</div>
            )}
            {/* Top-LEFT here: the close button holds the right. */}
            {isPremium(product) && (
              <span className="premium-badge is-left">Premium</span>
            )}
            {hasImage && <span className="pm-zoom-hint">🔍 Tap to zoom</span>}
          </div>

          <div className="pm-body">
            <h2 className="pm-title">{product.name}</h2>

            <p className="pm-planet">
              {product.emoji} Ruled by <strong>{product.planet}</strong>
            </p>

            <div className="pm-price-row">
              <span className="pm-price">{inr(product.price)}</span>
              {product.originalPrice > product.price && (
                <>
                  <span className="pm-old">{inr(product.originalPrice)}</span>
                  {discount > 0 && (
                    <span className="pm-discount">{discount}% off</span>
                  )}
                </>
              )}
            </div>

            {/* No star rating here. Nothing has been reviewed yet, so
                every product read "⭐ 0" — a rating of zero out of five
                is worse than no rating at all, and inventing one would
                be worse still. Bring this back when real reviews are
                approved in the admin panel.

                Stock counts are deliberately not shown either — an
                unavailable item is already clear from the button. */}

            {product.description && (
              <div className="pm-section">
                <h3>About This Stone</h3>
                {/* Full text — this is the whole point of the modal. */}
                <p>{product.description}</p>
              </div>
            )}

            {product.tags?.length > 0 && (
              <div className="pm-section">
                <h3>Highlights</h3>
                <div className="pm-tags">
                  {product.tags.map((tag) => (
                    <span key={tag} className="pm-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {product.category && (
              <div className="pm-section">
                <h3>Category</h3>
                <p className="pm-category">{categoryLabel(product.category)}</p>
              </div>
            )}
          </div>

          <div className="pm-actions">
            <button
              className={`pm-cart-btn ${added ? 'added' : ''}`}
              disabled={outOfStock}
              onClick={handleAdd}
            >
              {outOfStock ? (
                'Unavailable'
              ) : added ? (
                'Added to Bag ✓'
              ) : (
                <>
                  <HiShoppingBag />
                  <span>Add to Bag</span>
                </>
              )}
            </button>

            {/* Checkout lives in the bag now, so once something is in
                there, offer the way through to it. */}
            {added && (
              <Link className="pm-bag-link" to="/cart" onClick={onClose}>
                Review bag &amp; check out →
              </Link>
            )}
          </div>
        </motion.div>
      </div>

      {zoom && hasImage && (
        <ImageLightbox
          src={product.image}
          alt={product.name}
          onClose={() => setZoom(false)}
        />
      )}
    </>
  );
}
